import { useEffect, useRef, useState } from 'react';
// @ts-ignore
import debounce from 'lodash/debounce';
import { styled, TextField } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';

interface SearchInputProps {
  onDebouncedChange: (value: string) => void;
  debounceDelay?: number;
  label?: string;
  value: string;
}

function SearchInput({
  onDebouncedChange,
  debounceDelay = 500,
  label = 'Search',
  value,
}: Readonly<SearchInputProps>) {
  const [searchInput, setSearchInput] = useState(value || '');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedChangeHandler = useRef<ReturnType<typeof debounce> | null>(
    null,
  );

  useEffect(() => {
    const handler = debounce((value: string) => {
      onDebouncedChange(value);
    }, debounceDelay);

    debouncedChangeHandler.current = handler;

    return () => {
      if (debouncedChangeHandler.current) {
        debouncedChangeHandler.current.cancel();
      }
    };
  }, [onDebouncedChange, debounceDelay]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);

    if (debouncedChangeHandler.current) {
      debouncedChangeHandler.current(value);
    }
  };

  return (
    <SearchInputContainer>
      <TextField
        label={label}
        variant="outlined"
        sx={{
          width: 200,
          '& .MuiOutlinedInput-root': {
            borderRadius: '9px',
            background: '#fff',
            fontSize: 13,
            '& fieldset': { borderColor: '#E1E7F0' },
            '&:hover fieldset': { borderColor: '#C3D0E2' },
            '&.Mui-focused fieldset': {
              borderColor: '#1C71E3',
              boxShadow: '0 0 0 3px rgba(28,113,227,.12)',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#1C71E3' },
        }}
        size="small"
        value={searchInput}
        onChange={handleChange}
        ref={searchInputRef}
        InputProps={{
          endAdornment: (
            <ClearIcon
              onClick={() => {
                setSearchInput('');
                if (debouncedChangeHandler.current) {
                  debouncedChangeHandler.current('');
                }
              }}
              sx={{
                display: searchInput ? 'block' : 'none',
                cursor: 'pointer',
                height: 20,
                color: '#9BA6B7',
              }}
            />
          ),
        }}
      />
    </SearchInputContainer>
  );
}

const SearchInputContainer = styled('div')({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

export default SearchInput;
